import axios from 'axios';

import { env } from './runtime-env';

// Client SEM auth — endpoints públicos.
// Usa mesma URL base do client principal (env.apiUrl), pra dev (localhost:3000)
// e prod (domínio configurado em runtime) responderem corretamente.
const publicClient = axios.create({
  baseURL: env.apiUrl,
  withCredentials: false,
  timeout: 30_000,
});

const RESERVEI_CHECKOUT_INTENT_KEY = 'reservei-checkout-intent-v1';

/** Only same-origin paths are retained as return targets; absolute URLs are never trusted. */
export function normalizeReserveiReturnPath(value: unknown): string | undefined {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value !== value.trim() ||
    // Intencional: caractere de controle no caminho de volta é sinal de ataque
    // (quebra de linha, NUL) — recusa antes de montar a URL.
    // eslint-disable-next-line no-control-regex
    /[\\\u0000-\u001f\u007f]/.test(value) ||
    !value.startsWith('/') ||
    value.startsWith('//')
  ) {
    return undefined;
  }
  try {
    const parsed = new URL(value, 'https://reservei.invalid');
    if (parsed.origin !== 'https://reservei.invalid' || parsed.username || parsed.password) return undefined;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return undefined;
  }
}

function removeSavedIntent(): void {
  try {
    window.sessionStorage.removeItem(RESERVEI_CHECKOUT_INTENT_KEY);
  } catch {
    // SecurityError must not break checkout rendering.
  }
}

export interface ReserveiCheckoutIntent {
  handoffToken: string;
  planSlug?: string;
  cycle?: string;
  redirect?: string;
  expiresAt: number;
}

export function saveReserveiCheckoutIntent(
  intent: Omit<ReserveiCheckoutIntent, 'expiresAt'> & { expiresAt?: number },
): void {
  if (typeof window === 'undefined' || !intent.handoffToken) return;
  try {
    const safeRedirect = normalizeReserveiReturnPath(intent.redirect);
    window.sessionStorage.setItem(
      RESERVEI_CHECKOUT_INTENT_KEY,
      JSON.stringify({
        ...intent,
        redirect: safeRedirect,
        expiresAt: intent.expiresAt ?? Date.now() + 30 * 60_000,
      } satisfies ReserveiCheckoutIntent),
    );
  } catch {
    // Checkout must remain usable if the browser disables session storage.
  }
}

export function readReserveiCheckoutIntent(): ReserveiCheckoutIntent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(RESERVEI_CHECKOUT_INTENT_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<ReserveiCheckoutIntent>;
    if (
      typeof value.handoffToken !== 'string' ||
      value.handoffToken.length === 0 ||
      typeof value.expiresAt !== 'number' ||
      value.expiresAt <= Date.now()
    ) {
      removeSavedIntent();
      return null;
    }
    return {
      handoffToken: value.handoffToken,
      planSlug: typeof value.planSlug === 'string' ? value.planSlug : undefined,
      cycle: typeof value.cycle === 'string' ? value.cycle : undefined,
      redirect: normalizeReserveiReturnPath(value.redirect),
      expiresAt: value.expiresAt,
    };
  } catch {
    removeSavedIntent();
    return null;
  }
}

export function clearReserveiCheckoutIntent(): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(RESERVEI_CHECKOUT_INTENT_KEY);
  } catch {
    // No state remains to clear when session storage is unavailable.
  }
}

export interface PublicPlanInfo {
  slug: string;
  name: string;
  description: string | null;
  monthly_price: string;
  /** Preço do ciclo anual — null quando o plano não oferece anual. */
  annual_price: string | null;
  trial_days: number;
  /** `pagamento_unico` = serviço cobrado uma vez só (implantação), pelo `setup_fee`. */
  billing_cycle: string;
  /** Taxa única. Ausente em API antiga (até reiniciar). */
  setup_fee?: string;
  /** Destaques configurados no plano (admin). Vazio → o checkout usa o fallback padrão. */
  highlights?: string[];
  saas: {
    slug: string;
    name: string;
    url: string | null;
  } | null;
}

export interface PublicCheckoutCreditCard {
  number: string;
  holder_name: string;
  expiry_month: string; // MM
  expiry_year: string; // YYYY
  ccv: string;
  postal_code?: string;
  address_number?: string;
}

export interface PublicCheckoutPayload {
  plan_slug: string;
  email: string;
  name: string;
  cpf_cnpj: string;
  phone?: string;
  payment_method: 'pix' | 'boleto' | 'credit_card';
  /** Ciclo de cobrança — 'anual' só quando o plano tem annual_price. */
  billing_cycle?: 'mensal' | 'anual' | 'pagamento_unico';
  saas_origin?: string;
  handoff_token?: string;
  /**
   * Chave da tentativa (uuid), uma por visita à tela. O servidor guarda a
   * compra por ela: clique duplo ou reenvio devolve a MESMA cobrança.
   */
  checkout_key?: string;
  /** Cupom de desconto — aplicado só na 1ª cobrança. */
  coupon_code?: string;
  credit_card?: PublicCheckoutCreditCard;
  /** Aceite obrigatório dos Termos + Política (R1/LGPD). Sempre true — o backend exige. */
  terms_accepted: true;
  /** Versão dos termos aceita — registrada no consentimento (Subscription.termsVersion). */
  terms_version: string;
}

export interface PublicCheckoutResult {
  payment_url: string | null;
  subscription_id: string;
  client_id: string;
  charge_id: string | null;
  payment_method: 'pix' | 'boleto' | 'credit_card' | null;
  status: 'em_teste' | 'aguardando_pagamento';
  is_trial: boolean;
}

export interface PaymentDetails {
  status: string;
  payment_method: string | null;
  amount: string;
  due_date: string | null;
  payment_url: string | null;
  pix_qr_image: string | null;
  pix_payload: string | null;
  pix_expiration: string | null;
  bank_slip_url: string | null;
  bank_slip_line: string | null;
  bank_slip_bar_code: string | null;
}

export async function fetchPublicPlan(slug: string, saasOrigin?: string): Promise<PublicPlanInfo> {
  const { data } = await publicClient.get(`/api/public/checkout/plans/${slug}`, {
    params: saasOrigin ? { saas: saasOrigin } : undefined,
  });
  return data.data as PublicPlanInfo;
}

/**
 * Planos ativos de um SaaS, do mais barato pro mais caro (checkout próprio do
 * SaaS). O `slug` de cada um é o do SaaS — o mesmo que vai em `plan_slug`.
 */
export async function fetchPublicPlans(saas: string): Promise<PublicPlanInfo[]> {
  const { data } = await publicClient.get('/api/public/checkout/plans', { params: { saas } });
  return Array.isArray(data?.data) ? (data.data as PublicPlanInfo[]) : [];
}

export async function submitPublicCheckout(
  payload: PublicCheckoutPayload,
): Promise<PublicCheckoutResult> {
  const { data } = await publicClient.post('/api/public/checkout', payload);
  return data.data as PublicCheckoutResult;
}

export interface ValidateCouponPayload {
  code: string;
  plan_slug: string;
  saas_origin?: string;
  billing_cycle?: 'mensal' | 'anual' | 'pagamento_unico';
  email?: string;
}

export type CouponValidationResult =
  | {
      valid: true;
      code: string;
      discount_type: 'percent' | 'fixed';
      original_price: number;
      discount_amount: number;
      final_price: number;
    }
  | { valid: false; message: string };

/**
 * Valida/preview um cupom de desconto. Retorna o preço final pra UI mostrar —
 * mas quem cobra de verdade é o backend no submit (nunca confiar neste valor).
 */
export async function validateCoupon(
  payload: ValidateCouponPayload,
): Promise<CouponValidationResult> {
  const { data } = await publicClient.post('/api/public/checkout/validate-coupon', payload);
  return data.data as CouponValidationResult;
}

export async function fetchPaymentDetails(chargeId: string): Promise<PaymentDetails> {
  const { data } = await publicClient.get(`/api/public/checkout/payment/${chargeId}`);
  return data.data as PaymentDetails;
}

export async function fetchPaymentStatus(
  chargeId: string,
): Promise<{ status: string; paid: boolean }> {
  const { data } = await publicClient.get(`/api/public/checkout/payment/${chargeId}/status`);
  return data.data as { status: string; paid: boolean };
}
